import React, { useState, useEffect } from 'react';
import '../pages/OrgChart.css';

export default function AddEntityModal({ nodes, isOpen, initialData, onClose, onCreated }) {
  // Ensure nodes always is an array
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  // remember the original ID so we don’t block unchanged IDs
  const originalId = initialData?.id || "";
  // Form state with default arrays
  // Form state with default values
const [form, setForm] = useState({
    id: '',
    entity: '',
    trading_as: '',
    activity: '',
    domiciled: '',
    incorporated: '',
    date_of_incorporation: '',
    parent_ids: [],
    shareholders: [{ id: '', share_class: '', percentage: '' }],
    assets: [{ value: '', description: '' }],
    debts: [{ amount: '', institution: '', rate: '', maturity: '', term: '' }],
    free_cashflow: '',
    fair_market_value: '',
    value_description: ''
  });
  
  // Prefill or reset the form when modal opens/closes
  useEffect(() => {
    if (isOpen && initialData) {
      setForm({
        id: initialData.id || '',
        entity: initialData.entity || '',
        trading_as: initialData.trading_as || '',
        activity: initialData.activity || '',
        domiciled: initialData.domiciled || '',
        incorporated: initialData.incorporated || '',
        date_of_incorporation: initialData.date_of_incorporation || '',
        parent_ids: initialData.parent_ids || [],
        shareholders: initialData.shareholders?.map(sh => ({
          id: sh.id,
          share_class: sh.share_class || '',
          percentage: sh.percentage?.toString() || ''
        })) || [{ id: '', share_class: '', percentage: '' }],
        assets: initialData.assets?.map(a => ({
          value: a.value?.toString() || '',
          description: a.description || ''
        })) || [{ value: '', description: '' }],
        debts: initialData.debts?.map(d => ({
          amount: d.amount?.toString() || '',
          institution: d.institution || '',
          rate: d.rate?.toString() || '',
          maturity: d.maturity || '',
          term: d.term || ''
        })) || [{ amount: '', institution: '', rate: '', maturity: '', term: '' }],
        free_cashflow: initialData.free_cashflow?.toString() || '',
        fair_market_value: initialData.fair_market_value?.toString() || '',
        value_description: initialData.value_description || ''
      });
      setError('');
    } else if (!isOpen) {
      setForm({
        id: '',
        entity: '',
        trading_as: '',
        activity: '',
        domiciled: '',
        incorporated: '',
        date_of_incorporation: '',
        parent_ids: [],
        shareholders: [{ id: '', share_class: '', percentage: '' }],
        assets: [{ value: '', description: '' }],
        debts: [{ amount: '', institution: '', rate: '', maturity: '', term: '' }],
        free_cashflow: '',
        fair_market_value: '',
        value_description: ''
      });
      setError('');
    }
  }, [initialData, isOpen]);
  
  // Validation error message
  const [error, setError] = useState('');


  // Safe derived arrays
  const safeShareholders = Array.isArray(form.shareholders) ? form.shareholders : [];
  const safeAssets       = Array.isArray(form.assets) ? form.assets : [];
  const safeDebts        = Array.isArray(form.debts) ? form.debts : [];

  if (!isOpen) return null;

  const updateField = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    if (key === 'id') setError('');
  };  
  // Dynamic list handlers
  const handleListChange = (listName, idx, field, value) => {
    const list = Array.isArray(form[listName]) ? [...form[listName]] : [];
    list[idx][field] = value;
    setForm(f => ({ ...f, [listName]: list }));
  };
  const addListItem = listName => {
    const newItem = listName === 'shareholders'
      ? { id: '', share_class: '', percentage: '' }
      : listName === 'assets'
      ? { value: '', description: '' }
      : { amount: '', institution: '', rate: '', maturity: '', term: '' };
    const list = Array.isArray(form[listName]) ? [...form[listName], newItem] : [newItem];
    setForm(f => ({ ...f, [listName]: list }));
  };
  const removeListItem = (listName, idx) => {
    const list = Array.isArray(form[listName]) ? form[listName].filter((_, i) => i !== idx) : [];
    setForm(f => ({ ...f, [listName]: list }));
  };

  const handleSubmit = async () => {
     // 0) Validate ID: numeric only, max 3 digits
     if (!/^\d{1,3}$/.test(form.id)) {
       setError('ID must be numeric with up to 3 digits (e.g. 001).');
       return;
     }
     // 1) No duplicate IDs (unless the ID itself is unchanged)
     if (form.id !== originalId && safeNodes.some(n => n.id === form.id)) {
       setError(`ID "${form.id}" is already in use.`);
       return;
     }

    // Build a clean payload, dropping any blank shareholders/assets/debts
    const payload = {
      id: form.id,
      entity: form.entity,
      trading_as: form.trading_as || null,
      activity: form.activity || null,
      domiciled: form.domiciled || null,
      incorporated: form.incorporated || null,
      date_of_incorporation: form.date_of_incorporation || null,
      parent_ids: (form.shareholders || [])
        .map(sh => sh.id)
        .filter(id => id),
      shareholders: (form.shareholders || [])
        .filter(sh => sh.id.trim())
        .map(sh => ({
          id: sh.id,
          share_class: sh.share_class || null,
          percentage: sh.percentage ? parseFloat(sh.percentage) : null
        })),
      assets: (form.assets || [])
        .map(a => ({
          value: a.value ? parseFloat(a.value) : null,
          description: a.description || null
        }))
        .filter(a => a.value != null),
      debts: (form.debts || [])
        .map(d => ({
          amount: d.amount ? parseFloat(d.amount) : null,
          institution: d.institution || null,
          rate: d.rate ? parseFloat(d.rate) : null,
          maturity: d.maturity || null,
          term: d.term || null
        }))
        .filter(d => d.amount != null),
      free_cashflow: form.free_cashflow ? parseFloat(form.free_cashflow) : null,
      fair_market_value: form.fair_market_value ? parseFloat(form.fair_market_value) : null,
      value_description: form.value_description || null,
      // required by the backend: use existing coords for edits or default for creates
      position_x: initialData?.position_x ?? 20,
      position_y: initialData?.position_y ?? 48
    };

    // DEBUG: log which URL we’re using (and fall back to a relative path if env var is missing)
    const baseUrl = process.env.REACT_APP_BACKEND_URL_TOOLS || '';

    // Upsert full entity (create or update) via POST
    const res = await fetch(
        `${baseUrl}/org-entities/`,
        {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        }
    );
      
    // Handle errors
    if (!res.ok) {
      console.error('Create failed with status', res.status);
      return;
    }

    const data = await res.json();
    onCreated(data);
    onClose();
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 50 }} />
      <aside
        className="detail-panel"
        style={{ position: 'absolute', top: 0, right: 0, width: 300, height: '100%', backgroundColor: '#fff', color: '#000', zIndex: 60 }}
      >
        <h3>New Entity</h3>
        {error && (
          <div style={{ color: 'red', marginBottom: '1rem' }}>
            {error}
            </div>
        )}

        {/* Basic fields */}
        {[
          ['id', 'ID'],
          ['entity', 'Entity'],
          ['trading_as', 'Trading As'],
          ['activity', 'Activity'],
          ['domiciled', 'Domiciled'],
          ['incorporated', 'Incorporated'],
          ['date_of_incorporation', 'Date of Incorporation'],
          ['free_cashflow', 'Free Cashflow'],
          ['fair_market_value', 'Net Equity'],
          ['value_description', 'Notes']
        ].map(([key, label]) => (
          <div className="detail-item" key={key} style={{ marginBottom: '1rem' }}>
            <strong>{label}:</strong>
            {key === 'date_of_incorporation' ? (
              <input
                  type="date"
                  style={{ marginLeft: '1rem', width: '70%' }}
                  value={form[key]}
                  onChange={e => updateField(key, e.target.value)}
                />
            ) : (
              <input
                style={{ marginLeft: '1rem', width: '70%' }}
                {...(key === 'id'
                  ? { maxLength: 3, inputMode: 'numeric', pattern: '\\d*' }
                  : {})}
                value={form[key]}
                onChange={e => updateField(key, e.target.value)}
              />
            )}
          </div>
        ))}
        
        {/* Shareholders */}
        <div className="detail-item" style={{ marginBottom: '1rem' }}>
          <strong>Shareholders:</strong>
          {safeShareholders.map((sh, i) => (
            <div
              key={i}
              style={{ display: 'flex', alignItems: 'center', marginLeft: '1rem', marginBottom: '0.5rem' }}
            >
              <select
                value={sh.id}
                onChange={e => handleListChange('shareholders', i, 'id', e.target.value)}
              >
                <option value="" disabled hidden>Select</option>
                {safeNodes.filter(n => n.id !== form.id).map(n => (
                  <option key={n.id} value={n.id}>{n.data.label}</option>
                ))}
              </select>
              <input
                placeholder="Class"
                value={sh.share_class}
                onChange={e => handleListChange('shareholders', i, 'share_class', e.target.value)}
                style={{ marginLeft: '0.5rem' }}
              />
              <input
                placeholder="%"
                type="number"
                value={sh.percentage}
                onChange={e => handleListChange('shareholders', i, 'percentage', e.target.value)}
                style={{ width: 60, marginLeft: '0.5rem' }}
              />
              <button
                onClick={() => removeListItem('shareholders', i)}
                style={{ marginLeft: '0.5rem' }}
              >
                ×
              </button>
            </div>
          ))}
          <button onClick={() => addListItem('shareholders')} style={{ marginLeft: '1rem' }}>
            + Add
          </button>
        </div>

        {/* Assets */}
        <div className="detail-item" style={{ marginBottom: '1rem' }}>
          <strong>Assets:</strong>
          {safeAssets.map((a, i) => (
            <div
              key={i}
              style={{ display: 'flex', alignItems: 'center', marginLeft: '1rem', marginBottom: '0.5rem' }}
            >
              <input
                placeholder="Value"
                type="number"
                value={a.value}
                onChange={e => handleListChange('assets', i, 'value', e.target.value)}
                style={{ width: 80 }}
              />
              <input
                placeholder="Description"
                value={a.description}
                onChange={e => handleListChange('assets', i, 'description', e.target.value)}
                style={{ marginLeft: '0.5rem', flex: 1 }}
              />
              <button
                onClick={() => removeListItem('assets', i)}
                style={{ marginLeft: '0.5rem' }}
              >
                ×
              </button>
            </div>
          ))}
          <button onClick={() => addListItem('assets')} style={{ marginLeft: '1rem' }}>
            + Add Asset
          </button>
        </div>

        {/* Debts */}
        <div className="detail-item" style={{ marginBottom: '1rem' }}>
          <strong>Debts:</strong>
          {safeDebts.map((d, i) => (
            <div
              key={i}
              style={{ display: 'flex', alignItems: 'center', marginLeft: '1rem', marginBottom: '0.5rem' }}
            >
              <input
                placeholder="Amt"
                type="number"
                value={d.amount}
                onChange={e => handleListChange('debts', i, 'amount', e.target.value)}
                style={{ width: 80 }}
              />
              <input
                placeholder="Inst"
                value={d.institution}
                onChange={e => handleListChange('debts', i, 'institution', e.target.value)}
                style={{ marginLeft: '0.5rem', flex: 1 }}
              />
              <input
                placeholder="% Rate"
                type="number"
                value={d.rate}
                onChange={e => handleListChange('debts', i, 'rate', e.target.value)}
                style={{ width: 60, marginLeft: '0.5rem' }}
              />
              <input
                placeholder="Term"
                value={d.term}
                onChange={e => handleListChange('debts', i, 'term', e.target.value)}
                style={{ width: 80, marginLeft: '0.5rem' }}
              />
              <input
                placeholder="Maturity"
                type="date"
                value={d.maturity}
                onChange={e => handleListChange('debts', i, 'maturity', e.target.value)}
                style={{ marginLeft: '0.5rem', flex: 1 }}
              />
              <button
                onClick={() => removeListItem('debts', i)}
                style={{ marginLeft: '0.5rem' }}
              >
                ×
              </button>
            </div>
          ))}
          <button onClick={() => addListItem('debts')} style={{ marginLeft: '1rem' }}>
            + Add Debt
          </button>
        </div>

        <div className="detail-item" style={{ marginTop: '1.5rem' }}>
          <button onClick={handleSubmit}>
            {initialData ? 'Save' : 'Create'}
          </button>
          <button onClick={onClose} style={{ marginLeft: '0.5rem' }}>
            Cancel
          </button>
        </div>
      </aside>
    </>
  );
}